import {
  SearchBox,
  Toolbar,
  ToolbarButton,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  Tooltip,
  RadioGroup,
  Radio,
  Dropdown,
  Option,
} from "@fluentui/react-components";
import {
  ArrowClockwiseRegular,
  DocumentPersonRegular,
  AddRegular,
  ChevronDownRegular,
  ArrowLeftRegular,
  VehicleCarRegular,
  ShieldRegular,
} from "@fluentui/react-icons";
import { useNavigate } from "react-router-dom";
import { routes } from "config/routes";
import { useAppTheme } from "hooks/useAppTheme";
import { useTranslation } from "react-i18next";
import { useHasAnyRole } from "hooks/useUserRoles";
import { ResultsScore } from "components/ResultsScore";
import { RolePermissions } from "library/constants/roles";
import * as Sentry from "@sentry/react";
import { OffersShowMode } from "state/queries/useGetOffers";

interface Props {
  columnOptions?: JSX.Element;
  displaySearch?: boolean;
  displayReload?: boolean;
  rightSideButtons?: JSX.Element;
  onReload?: () => void;
  onSearch?: (searchValue: string) => void;
  searchValue?: string;
  showNewMenu?: boolean;
  showCascoConditionsLink?: boolean;
  productFilter?: "all" | "mtpl" | "casco";
  onProductFilterChange?: (value: "all" | "mtpl" | "casco") => void;
  /** shown next to the search box only when onShowModeChange is given (admins and read-only admins) */
  showMode?: OffersShowMode;
  onShowModeChange?: (value: OffersShowMode) => void;
  totalCount?: number;
}

export const Navigation = ({
  columnOptions,
  displaySearch = false,
  displayReload = false,
  rightSideButtons,
  onReload,
  onSearch,
  searchValue,
  showNewMenu = false,
  showCascoConditionsLink = true,
  productFilter = "all",
  onProductFilterChange,
  showMode = "valid",
  onShowModeChange,
  totalCount,
}: Props) => {
  const navigate = useNavigate();
  const theme = useAppTheme();
  const { t } = useTranslation();

  // Check if user can view settings (admins and read-only admins)
  const canViewSettings = useHasAnyRole(RolePermissions.canViewSettings());

  const showModeLabels: Record<OffersShowMode, string> = {
    all: t("qtg.proposalList.showMode.all", "All entries"),
    valid: t("qtg.proposalList.showMode.valid", "Valid entries"),
    error: t("qtg.proposalList.showMode.error", "Error entries"),
  };

  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap", // on narrow screens the right side moves to a second row instead of overlapping the left one
        justifyContent: "space-between",
        alignItems: "center",
        width: "100%",
        padding: "0 16px",
        boxSizing: "border-box",
        borderBottom: theme.isDark
          ? `1px solid ${theme.semanticColors.bodyFrameDivider}`
          : `1px solid ${theme.semanticColors.bodyFrameDivider}`,
        background: theme.isDark
          ? theme.semanticColors.bodyFrameBackground
          : theme.semanticColors.bodyFrameBackground,
      }}
    >
      {/* Left side of the toolbar */}
      <Toolbar
        size="large"
        aria-label="Toolbar with buttons"
        style={{ flex: 1, whiteSpace: "nowrap" }}
      >
        {showNewMenu ? (
          <Menu>
            <MenuTrigger>
              <ToolbarButton
                data-help="offers-new-menu"
                aria-label="New"
                appearance="primary"
                icon={<AddRegular />}
              >
                {t("qtg.navigation.newProposal", "New Proposal")}
                <ChevronDownRegular />
              </ToolbarButton>
            </MenuTrigger>
            <MenuPopover>
              <MenuList>
                <MenuItem
                  data-help="offers-new-mtpl"
                  icon={<DocumentPersonRegular />}
                  onClick={() => {
                    navigate(routes.offer.path + "?type=mtpl", {
                      replace: true,
                    });
                  }}
                >
                  {t("qtg.navigation.newMtplProposal", "New MTPL Proposal")}
                </MenuItem>
                <MenuItem
                  data-help="offers-new-casco"
                  icon={<DocumentPersonRegular />}
                  onClick={() => {
                    navigate(routes.offer.path + "?type=casco", {
                      replace: true,
                    });
                  }}
                >
                  {t("qtg.navigation.newCascoProposal", "New Casco Proposal")}
                </MenuItem>
              </MenuList>
            </MenuPopover>
          </Menu>
        ) : (
          <Tooltip
            content={t("qtg.navigation.back.tooltip", "Go back to offers list")}
            relationship="label"
          >
            <ToolbarButton
              data-help="offer-back"
              aria-label="Back"
              appearance="subtle"
              icon={<ArrowLeftRegular />}
              onClick={() => {
                navigate(routes.offers.path);
              }}
            />
          </Tooltip>
        )}
        {displaySearch && (
          <SearchBox
            data-help="offers-search"
            placeholder={t("greco.form.searchPlaceholder")}
            // width = the narrowest it may get (instead of the input's natural width); flex grows it into the free space
            style={{ flex: 1, width: "120px", minWidth: "120px", margin: "0 16px" }}
            value={searchValue || ""}
            onChange={(_, data) => {
              onSearch && onSearch(data.value);
            }}
          />
        )}
        {displaySearch && onShowModeChange && (
          <Dropdown
            data-help="offers-show-mode"
            aria-label={t("qtg.proposalList.showMode.label", "Show entries")}
            value={showModeLabels[showMode]}
            selectedOptions={[showMode]}
            onOptionSelect={(_, data) =>
              data.optionValue &&
              onShowModeChange(data.optionValue as OffersShowMode)
            }
            style={{ minWidth: "150px", marginRight: "16px" }}
          >
            {(["all", "valid", "error"] as OffersShowMode[]).map((mode) => (
              <Option key={mode} value={mode}>
                {showModeLabels[mode]}
              </Option>
            ))}
          </Dropdown>
        )}
        {displayReload && (
          <Tooltip
            content={t("qtg.navigation.reload.tooltip", "Reload offers list")}
            relationship="label"
          >
            <ToolbarButton
              data-help="offers-reload"
              aria-label="Reload"
              appearance="subtle"
              icon={<ArrowClockwiseRegular />}
              onClick={onReload}
            />
          </Tooltip>
        )}
        {onProductFilterChange && (
          <RadioGroup
            data-help="offers-product-filter"
            value={productFilter}
            onChange={(_, data) =>
              onProductFilterChange(data.value as "all" | "mtpl" | "casco")
            }
            layout="horizontal"
            style={{ marginLeft: "16px", gap: "8px", whiteSpace: "nowrap" }}
          >
            <Radio
              value="all"
              label={t("qtg.proposalList.filter.all", "All")}
            />
            <Radio
              value="mtpl"
              label={
                <span
                  style={{ display: "flex", alignItems: "center", gap: "6px" }}
                >
                  <ShieldRegular
                    style={{ fontSize: "16px", color: "#d83b01" }}
                  />
                  {t("qtg.proposalList.filter.mtpl", "MTPL")}
                </span>
              }
            />
            <Radio
              value="casco"
              label={
                <span
                  style={{ display: "flex", alignItems: "center", gap: "6px" }}
                >
                  <VehicleCarRegular
                    style={{ fontSize: "16px", color: "#0078d4" }}
                  />
                  {t("qtg.proposalList.filter.casco", "Casco")}
                </span>
              }
            />
          </RadioGroup>
        )}
      </Toolbar>

      {/* Right side of the toolbar - never shrinks, so button labels stay on one line; the search box gives way instead */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          flexShrink: 0,
          marginLeft: "auto",
          whiteSpace: "nowrap",
        }}
      >
        {rightSideButtons}
        {showCascoConditionsLink && canViewSettings && (
          <Tooltip
            content={t("qtg.settings.cascoConditions.title")}
            relationship="label"
          >
            <ToolbarButton
              data-help="offers-casco-conditions"
              aria-label="CASCO Conditions"
              appearance="subtle"
              onClick={() => {
                Sentry.logger.info("User clicked on CASCO Conditions", {});
                navigate("/settings/casco-conditions");
              }}
            >
              {t("qtg.settings.cascoConditions.title")}
            </ToolbarButton>
          </Tooltip>
        )}
        {columnOptions}
        {totalCount !== undefined && (
          <div data-help="offers-result-count" style={{ display: "flex", alignItems: "center" }}>
            <ResultsScore score={totalCount} />
          </div>
        )}
      </div>
    </div>
  );
};
